import { createRef, CSSProperties, useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { replaceTinyContent } from "src/ui/components/Tiny/utils";
import { fixImageMaps } from "src/utils";
import PhotoBox from "../../PhotoBox/PhotoBox";
import UnsplashAttribution from "../../UnsplashAttribution";
import { useSelector } from "react-redux";
import "./TinyFormattedContent.scss";
import { createRoot } from "react-dom/client";

export const wrapTablesWithDiv = (tables: NodeListOf<HTMLTableElement> | HTMLTableElement[]) => {
    Array.from(tables).forEach((table) => {
        if (!table?.parentNode) {
            return;
        }

        const wrapper = document.createElement("div");
        wrapper.className = "table-wrapper";

        table.parentNode.replaceChild(wrapper, table);
        wrapper.appendChild(table);
    });
};

type Props = {
    attachedFiles?: unknown[] | undefined;
    attachedFolders?: unknown[] | undefined;
    attachedSharePointListSearches?: unknown[] | undefined;
    content: string;
    language?: string | undefined;
    className?: string | undefined;
};

type StateProps = {
    isInTeamsApp: boolean;
};

type IState = {
    photoBoxImageUrl: string | null;
    ranReplacer?: boolean;
};

function CSSstring(text: string): CSSProperties | undefined {
    let obj: string;
    try {
        obj = JSON.parse(`{"${text
        .replace(/; /g, '", "')
        .replace(/: /g, '": "')
        .replace(";", "")}"}`);
    } catch {
        return undefined
    }

    const keyValues = Object.keys(obj).map((key) => {
        const camelCased = key.replace(/-[a-z]/g, (g) => g[1].toUpperCase());
        return { [camelCased]: obj[key] };
    });

    return Object.assign({}, ...keyValues);
}

function TinyFormattedContent(props: Props): JSX.Element {
    const history = useHistory();
    const {
        isInTeamsApp,
    } = useSelector<Spintr.AppState, StateProps>((state) => ({
        isInTeamsApp: state.ui.isInTeamsApp,
    }));

    const ownRef = createRef<HTMLDivElement>();
    const [state, setState] = useState<IState>({
        photoBoxImageUrl: null,
        ranReplacer: false,
    });

    const closePhotoBox = useCallback(() => setState((prev) => ({
        ...prev,
        photoBoxImageUrl: null,
    })), [setState]);
    
    const runReplacer = useCallback(() => {
        setState((prev) => ({
            ...prev,
            ranReplacer: true,
        }));

        if (ownRef.current === null) {
            return;
        }

        
        const thisDOM = ownRef.current;
        const imgTags = thisDOM.querySelectorAll("img");

        if (imgTags && imgTags.length > 0) {
            for (const imgTag of imgTags) {
                const isReact = !!Object.keys(imgTag).find((key) => key.startsWith("__reactFiber$"));

                if (isReact) {
                    continue;
                }

                // create parent that gets replaced later, to avoid the new element
                // getting appended to the old <a>
                const parent = document.createElement("span");
                const hasUnsplashAttribution = !!imgTag.dataset.externalAuthorName;
                parent.className = hasUnsplashAttribution ? "unsplash-attribution-wrapper" : "";
                if (hasUnsplashAttribution) {
                    parent.style.float = imgTag.style.float;
                    parent.style.marginLeft = imgTag.style["margin-left"];
                    parent.style.marginRight = imgTag.style["margin-right"];
                }

                const openPhotobox = (target: HTMLImageElement) => {
                    if (imgTag.useMap || target.parentElement.getAttribute("target") === "_blank") {
                        return;
                    }

                    setState((prev) => ({
                        ...prev,
                        photoBoxImageUrl: imgTag.src,
                    }));
                };

                createRoot(parent).render(
                    <>
                        <img
                            // TODO: Wrap this with a focusable element and add keyboard support for opening the photobox
                            // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role
                            role="button"
                            tabIndex={0}
                            src={imgTag.src}
                            alt={imgTag.alt}
                            height={imgTag.height}
                            width={imgTag.width}
                            onLoad={() => {
                                if (imgTag.useMap) {
                                    fixImageMaps();
                                }
                            }}
                            onKeyDown={(e) => {
                                if (e.key !== "Enter" && e.key !== " ") {
                                    return;
                                }

                                e.preventDefault();
                                openPhotobox(e.currentTarget);
                            }}
                            onClick={(e) => {
                                openPhotobox(e.currentTarget);
                            }}
                            style={{
                                // @ts-expect-error We're just copying from one element to another so it's fine
                                float: hasUnsplashAttribution ? undefined : imgTag.style.float,
                                display: imgTag.style.display,
                                marginLeft: hasUnsplashAttribution ? undefined : imgTag.style["margin-left"],
                                marginRight: hasUnsplashAttribution ? undefined : imgTag.style["margin-right"],
                                cursor: imgTag.useMap ? "default" : "pointer",
                            }}
                            useMap={imgTag.useMap}
                        />
                        {hasUnsplashAttribution && (
                            <UnsplashAttribution
                                authorName={imgTag.dataset.externalAuthorName}
                                authorUrl={imgTag.dataset.externalAuthorUrl}
                            />
                        )}
                    </>,
                );

                setTimeout(() => {
                    imgTag.replaceWith(parent);
                }, 1);
            }
        }

        const tables = thisDOM.querySelectorAll<HTMLTableElement>("table:not(.TinyFormTable-table)");
        wrapTablesWithDiv(tables);
        
        const aLinks = thisDOM.querySelectorAll("a:not(.fixed)");
        if (aLinks.length === 0) {
            return;
        }

        for (const aLink of aLinks) {
            if (aLink instanceof HTMLAnchorElement === false) {
                continue;
            }

            // ignore react components
            const isReact = !!Object.keys(aLink).find((key) => key.startsWith("__reactFiber$"));

            if (isReact) {
                continue;
            }

            // create parent that gets replaced later, to avoid the new element
            // getting appended to the old <a>
            const parent = document.createElement("span");
            const newStyle = aLink.style?.cssText ? CSSstring(aLink.style.cssText) : undefined;

            let target = aLink.target;

            if (aLink.hostname == window.location.hostname && isInTeamsApp) {
                target = "_self";
            }

            createRoot(parent).render(
                <a
                    className="fixed underline"
                    href={aLink.href}
                    target={target}
                    title={aLink.title}
                    rel={aLink.rel}
                    style={newStyle}
                    onClick={(e) => {
                        if (
                            !(target && target === "_blank") &&
                            aLink.hostname == window.location.hostname &&
                            !aLink.pathname.startsWith("/api/") &&
                            !aLink.pathname.startsWith("/media/usercontent/")
                        ) {
                            e.preventDefault();
                            e.stopPropagation();

                            if (!!aLink.hash &&
                                aLink.pathname === window.location.pathname) {
                                const id = aLink.hash.replace('#', '');
                                const element = document.getElementById(id);

                                if (element) {
                                    element.scrollIntoView();

                                    return;
                                }
                            }

                            history.push({
                                pathname: aLink.pathname,
                                hash: aLink.hash,
                                search: aLink.search,
                            });
                        }
                    }}
                    dangerouslySetInnerHTML={{ __html: aLink.innerHTML }}
                />,
            );
            
            setTimeout(() => {
                aLink.replaceWith(parent);
            }, 1);
        }
    }, [setState, ownRef, history, isInTeamsApp]);

    useEffect(() => {
        if (state.ranReplacer) {
            return;
        }

        const timeout = setTimeout(runReplacer, 1000);

        return () => clearTimeout(timeout);
    }, [setState, state.ranReplacer, runReplacer]);

    return (
        <div
            className={["TinyFormattedContent", props?.className && props.className].filter(Boolean).join(" ")}
            lang={props.language ? props.language : undefined}
            ref={ownRef}
        >
            {replaceTinyContent(
                props.content,
                props.attachedFiles,
                props.attachedFolders,
                props.attachedSharePointListSearches
            ).content}
            {state.photoBoxImageUrl && (
                <PhotoBox
                    imageInChat={true}
                    imageUrl={state.photoBoxImageUrl}
                    onClose={closePhotoBox} />
            )}
        </div>
    );
}

export default TinyFormattedContent;
